/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `qrPayloadParsers` for the Caintra Connect frontend.
 * @module utils/qrScan/qrPayloadParsers
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
const invalid = (errorStatus) => ({ valid: false, errorStatus });

const valid = (payload) => ({ valid: true, ...payload });

/**
 * Parses a QR payload and returns null when the text is not a JSON object.
 * Callers treat null as an invalid code instead of letting JSON.parse throw.
 * @param {string} decodedText
 * @returns {Object|null}
 */
export const parseQrJson = (decodedText) => {
  try {
    const parsed = JSON.parse(decodedText);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Validates registration event QR payload.
 * @param {string} decodedText
 * @param {string} selectedEventId
 * @returns {Object}
 */
export const parseRegistrationEventPayload = (decodedText, selectedEventId) => {
  const data = parseQrJson(decodedText);

  if (
    !data ||
    typeof data !== "object" ||
    !data.userId ||
    !data.eventId ||
    typeof data.userId !== "string" ||
    typeof data.eventId !== "string"
  ) {
    return invalid("invalidQRCode");
  }

  if (data.eventId !== selectedEventId) {
    return invalid("invalidEvent");
  }

  return valid({ userId: data.userId, eventId: data.eventId });
};

/**
 * Validates business event QR payload.
 * @param {string} decodedText
 * @param {string} selectedEventId
 * @returns {Object}
 */
export const parseBusinessEventPayload = (decodedText, selectedEventId) => {
  const data = parseQrJson(decodedText);

  if (
    !data ||
    typeof data !== "object" ||
    !data.userId ||
    !data.eventId ||
    !data.buyerCompanyId ||
    !data.appointmentId ||
    typeof data.userId !== "string" ||
    typeof data.eventId !== "string" ||
    typeof data.buyerCompanyId !== "string" ||
    typeof data.appointmentId !== "string"
  ) {
    return invalid("invalidQRCode");
  }

  if (data.eventId !== selectedEventId) {
    return invalid("invalidEvent");
  }

  return valid({
    userId: data.userId,
    eventId: data.eventId,
    buyerCompanyId: data.buyerCompanyId,
    appointmentId: data.appointmentId,
  });
};

/**
 * Validates B2B event QR payload (supports legacy key aliases).
 * @param {string} decodedText
 * @param {string} selectedEventId
 * @returns {Object}
 */
export const parseB2BEventPayload = (decodedText, selectedEventId) => {
  const data = parseQrJson(decodedText);
  const userId = data?.userId;
  const eventId = data?.idEventoB2B || data?.eventId;
  const appointmentId = data?.idCita || data?.appointmentId;

  if (
    !data ||
    typeof data !== "object" ||
    !userId ||
    !eventId ||
    !appointmentId
  ) {
    return invalid("invalidQRCode");
  }

  if (String(eventId) !== String(selectedEventId)) {
    return invalid("invalidEvent");
  }

  return valid({ userId, idEventoB2B: eventId, idCita: appointmentId });
};

/**
 * Validates breakfast event QR payload.
 * @param {string} decodedText
 * @param {string} selectedEventId
 * @returns {Object}
 */
export const parseBreakfastEventPayload = (decodedText, selectedEventId) => {
  const data = parseQrJson(decodedText);

  if (
    !data ||
    typeof data !== "object" ||
    !data.userId ||
    !data.idEventoDesayuno
  ) {
    return invalid("invalidQRCode");
  }

  if (data.idEventoDesayuno !== selectedEventId) {
    return invalid("invalidEvent");
  }

  return valid({
    userId: data.userId,
    idEventoDesayuno: data.idEventoDesayuno,
  });
};
